import { useEffect, useState } from 'react'
import type { HouseholdMember } from '@domain/household'
import { useHouseholdRepository } from '@ui/household/household-context'

/**
 * Os membros da casa, pela porta. Três estados, e nenhum deles é "lista vazia
 * porque ainda não carregou": lista vazia e lista não carregada dizem coisas
 * diferentes na tela, e um array só não distingue as duas.
 */
export type MembersState =
  | { status: 'loading' }
  | { status: 'loaded'; members: HouseholdMember[] }
  | { status: 'failed' }

export function useMembers(): MembersState {
  const repository = useHouseholdRepository()
  const [state, setState] = useState<MembersState>({ status: 'loading' })

  useEffect(() => {
    // `live` porque a resposta pode chegar depois do desmonte — sair da tela
    // enquanto a leitura está no ar é o caminho comum, não a exceção.
    let live = true

    // Sem `setState({ status: 'loading' })` na entrada: o estado inicial já é
    // esse, e `react-hooks/set-state-in-effect` reprova o setState síncrono
    // dentro do efeito. O reset só faria diferença se `repository` trocasse de
    // identidade em tempo de execução, e ele é injetado uma vez na composição.
    void repository
      .listMembers()
      .then((members) => {
        if (live) setState({ status: 'loaded', members })
      })
      .catch((error: unknown) => {
        // Falha vira estado visível **e** fica no console. Engolir aqui
        // produziria a lista vazia que a tela leria como "só você entrou".
        console.error('useMembers: listMembers falhou', error)
        if (live) setState({ status: 'failed' })
      })

    return () => {
      live = false
    }
  }, [repository])

  return state
}
